import React, { useState, useEffect } from 'react';

type Step = 'greeting' | 'filial' | 'selection';

const filiais = [
  { id: 'FILIAL_1', nome: 'UP Distribuidora', logo: '/Filial 1- UP DISTRIBUIDORA.png' },
  { id: 'FILIAL_2', nome: 'Aleplast', logo: '/Filial 2- Aleplast.png' },
];

const whatsappNumbers: Record<string, Record<string, string>> = {
  FILIAL_1: {
    agendamento: '5582993309424',
    suporte:     '5582994150472',
    compras:     '5582998226429',
    recebimento: '5582993387720',
  },
  FILIAL_2: {
    compras: '5582998226429',
    suporte: '5582994150472',
  },
};

const contatosPorFilial: Record<string, { sector: string; emoji: string; label: string }[]> = {
  FILIAL_1: [
    { sector: 'agendamento', emoji: '📅', label: 'Dúvidas de agendamento' },
    { sector: 'suporte',     emoji: '🔧', label: 'Falar com suporte' },
    { sector: 'compras',     emoji: '🛒', label: 'Falar com equipe de compras' },
    { sector: 'recebimento', emoji: '📦', label: 'Falar com recebimento' },
  ],
  FILIAL_2: [
    { sector: 'compras', emoji: '🛒', label: 'Falar com equipe de compras' },
    { sector: 'suporte', emoji: '🔧', label: 'Falar com suporte' },
  ],
};

const WhatsAppWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>('greeting');
  const [greeting, setGreeting] = useState('');
  const [filialSelecionada, setFilialSelecionada] = useState<string | null>(null);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) setGreeting('Bom dia!');
    else if (hour >= 12 && hour < 18) setGreeting('Boa tarde!');
    else setGreeting('Boa noite!');
  }, []);

  const handleOpenChat = () => {
    setIsOpen(true);
    setCurrentStep('greeting');
    setFilialSelecionada(null);
  };

  const handleSelectFilial = (filialId: string) => {
    setFilialSelecionada(filialId);
    setCurrentStep('selection');
  };

  const handleSelectSector = (sector: string) => {
    const filial = filialSelecionada || 'FILIAL_1';
    const number = whatsappNumbers[filial]?.[sector] || whatsappNumbers['FILIAL_1']['suporte'];
    const message = encodeURIComponent('Olá! Vim pelo chat do Falcon Hub e preciso de ajuda.');
    window.open(`https://wa.me/${number}?text=${message}`, '_blank');
    setIsOpen(false);
  };

  const filialNome = filiais.find(f => f.id === filialSelecionada)?.nome || '';

  return (
    <>
      {/* Widget principal */}
      <div onClick={handleOpenChat} className="fixed bottom-6 right-6 z-50 group cursor-pointer">
        <div className="relative">
          <div className="absolute -top-20 right-0 bg-white text-gray-800 px-4 py-2 rounded-lg shadow-xl whitespace-nowrap">
            <p className="text-sm font-semibold">Tire suas dúvidas</p>
            <p className="text-xs text-green-600">Falcon Hub</p>
          </div>
          <div className="relative w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full shadow-2xl shadow-green-500/50 hover:shadow-green-500/70 transition-all duration-300 hover:scale-110 hover:-translate-y-1 flex items-center justify-center border-4 border-white">
            <img src="/Avatar de suporte Falcon Hub.png" alt="Falcon Hub WhatsApp" className="w-16 h-16 object-contain" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/30 to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Modal do Chatbot */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setIsOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
              <div className="flex items-center gap-4">
                <img src="/Avatar de suporte Falcon Hub.png" alt="Falcon Hub" className="w-16 h-16 rounded-full border-4 border-white object-contain" />
                <div>
                  <h3 className="text-xl font-bold">Falcon Hub</h3>
                  <p className="text-green-100 text-sm">
                    {filialSelecionada ? filialNome : 'Suporte Online'}
                  </p>
                </div>
              </div>
            </div>

            {/* Chat Content */}
            <div className="p-6 bg-gray-50 min-h-[300px]">
              {/* Etapa 1: Saudação */}
              {currentStep === 'greeting' && (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm max-w-[80%]">
                    <p className="text-gray-800">{greeting} Como posso ajudar você hoje?</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm max-w-[80%]">
                    <p className="text-gray-800">Por favor, selecione o setor que deseja contatar:</p>
                  </div>
                  <div className="space-y-3 mt-6">
                    <button
                      onClick={() => setCurrentStep('filial')}
                      className="w-full bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
                    >
                      Ver opções de contato
                    </button>
                  </div>
                </div>
              )}

              {/* Etapa 2: Seleção de filial */}
              {currentStep === 'filial' && (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm max-w-[80%]">
                    <p className="text-gray-800">Qual filial você deseja acessar?</p>
                  </div>
                  <div className="space-y-3 mt-4">
                    {filiais.map(filial => (
                      <button
                        key={filial.id}
                        onClick={() => handleSelectFilial(filial.id)}
                        className="w-full bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg border-2 border-green-500 hover:border-green-700 flex items-center gap-3"
                      >
                        <img src={filial.logo} alt={filial.nome} className="w-10 h-10 object-contain rounded" />
                        {filial.nome}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setCurrentStep('greeting')} className="text-sm text-gray-500 hover:text-gray-700 mt-2">
                    ← Voltar
                  </button>
                </div>
              )}

              {/* Etapa 3: Opções de contato por filial */}
              {currentStep === 'selection' && filialSelecionada && (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm max-w-[80%]">
                    <p className="text-gray-800">Escolha com quem deseja falar:</p>
                  </div>
                  <div className="space-y-3 mt-4">
                    {contatosPorFilial[filialSelecionada]?.map(contato => (
                      <button
                        key={contato.sector}
                        onClick={() => handleSelectSector(contato.sector)}
                        className="w-full bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg border-2 border-green-500 hover:border-green-700 flex items-center gap-3"
                      >
                        <span className="text-2xl">{contato.emoji}</span>
                        {contato.label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setCurrentStep('filial')} className="text-sm text-gray-500 hover:text-gray-700 mt-2">
                    ← Voltar
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-100 p-4 text-center">
              <button onClick={() => setIsOpen(false)} className="text-gray-600 hover:text-gray-800 text-sm font-medium">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppWidget;
